import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { toast } from 'react-toastify';
import SetContext from './SetContext';
import SelectTemplate from './SelectTemplate';
import ConfirmSend from './ConfirmSend';
import templates1 from '../../assets/templates1.png';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

// Typecast icon components for TypeScript compatibility
const FaCheckComponent =
  IoIosCheckmarkCircle as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Define Template interface here
interface Template {
  id: string;
  orgId: string;
  name: string;
  description: string;
  status: string;
  version: number;
  folderId: string;
  assetId: string | null;
  blocks: Array<{
    text: string;
    type: string;
  }>;
  styles: string;
  metadata: {
    category: string;
    tags: string[];
    ownership: {
      type: string;
      orgId: string;
      userId: string;
    };
    createdBy: string;
    createdAt: string;
    updatedAt: string;
  };
  capability: any;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl: string | null;
}

// Interface for campaign data passed from table
interface CampaignData {
  id: string;
  title: string;
  subject: string | null;
  type: string | null;
  status: 'DRAFT' | 'SCHEDULED' | 'COMPLETED' | 'SENT' | 'CANCELLED';
  templateId: string;
  senderProfile: {
    senderName: string;
    senderEmail: string;
  };
  segments: any[];
  segmentIds: string[];
  tags: any[];
  tagIds: string[];
  contactIds?: string[];
}

// Helper function to get category from template metadata
const getTemplateCategory = (template: Template): string => {
  return template.metadata?.category || 'Uncategorized';
};

// Main component for creating a new campaign
const CreateCampaign: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // DETERMINE IF EDIT MODE
  const isEditMode = !!id || !!(location.state as any)?.campaign;
  const campaignData = (location.state as any)?.campaign as CampaignData | undefined;



  // ⭐ Steps based on mode
  const steps = useMemo(() => {
    if (isEditMode) {
      return ['Select Template', 'Set Context', 'Confirm & Send'];
    }
    return ['Select Template', 'Set Context'];
  }, [isEditMode]);

  const [step, setStep] = useState<number>(0);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    'All templates',
  ]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUnsavedModal, setShowUnsavedModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [templatesData, setTemplatesData] = useState<Template[]>([]);
  const [categories, setCategories] = useState<string[]>(['All templates']);

  // Track original template ID for edit mode
  const [originalTemplateId, setOriginalTemplateId] = useState<string | undefined>(
    campaignData?.templateId
  );

  // FORM STATE - Pre-filled for edit mode
  const [campaignTitle, setCampaignTitle] = useState<string>(
    campaignData?.title || ''
  );
  const [receiver, setReceiver] = useState<string>(
    campaignData?.segmentIds?.join(', ') || ''
  );
  const [tags, setTags] = useState<string>(
    campaignData?.tagIds?.join(', ') || ''
  );
  const [subject, setSubject] = useState<string>(
    campaignData?.subject || ''
  );
  const [senderName, setSenderName] = useState<string>(
    campaignData?.senderProfile?.senderName || ''
  );
  const [senderEmail, setSenderEmail] = useState<string>(
    campaignData?.senderProfile?.senderEmail || ''
  );

  const [campaignId, setCampaignId] = useState<string | undefined>(
    campaignData?.id || id
  );

  // ⭐ NEW: Selection type and contacts state
  const [selectionType, setSelectionType] = useState<string>('');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

  // ⭐ NEW: stable original snapshot (never mutated by form edits)
  const [originalCampaign, setOriginalCampaign] = useState<{
    templateId?: string;
    title: string;
    receiver: string;
    tags: string;
    subject: string;
    senderName: string;
    senderEmail: string;
    selectedTemplateIndex: number | null;
    selectionType: string;
    selectedContacts: string[];
  } | null>(null);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const { token, tenantId, userId } = getAuthContext();

        if (!token || !tenantId) {
          toast.error('Authentication required');
          setLoading(false);
          return;
        }

        const orgId = 'org_123456';

        const response = await fetch(
          `${BASE_URL}/v1/templates/user-define?orgId=${orgId}&page=0&size=1000`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch templates: ${response.status}`);
        }

        const data = await response.json();
        const templates = data.content || [];

        setTemplatesData(templates);

        const uniqueCategories = Array.from(
          new Set(templates.map((tpl: Template) => getTemplateCategory(tpl)))
        ) as string[];
        setCategories(['All templates', ...uniqueCategories]);

        // If in edit mode, find matching template
        if (isEditMode && campaignData?.templateId) {
          const templateIndex = templates.findIndex(
            (tpl: Template) => tpl.id === campaignData.templateId
          );
          if (templateIndex !== -1) {
            setSelectedTemplate(templateIndex);
            setOriginalTemplateId(campaignData.templateId);

            // ⭐ Determine selection type from campaign data
            let initialSelectionType = '';
            let initialSelectedContacts: string[] = [];

            if (campaignData.segmentIds && campaignData.segmentIds.length > 0) {
              initialSelectionType = 'segments';
            } else if (campaignData.tagIds && campaignData.tagIds.length > 0) {
              initialSelectionType = 'tags';
            } else if (campaignData.contactIds && campaignData.contactIds.length > 0) {
              initialSelectionType = 'contactIds';
              initialSelectedContacts = campaignData.contactIds;
            } else {
              initialSelectionType = 'all';
            }

            setSelectionType(initialSelectionType);
            setSelectedContacts(initialSelectedContacts);

            // ⭐ Capture the original snapshot once
            setOriginalCampaign({
              templateId: campaignData.templateId,
              title: campaignData.title || '',
              receiver: campaignData.segmentIds?.join(', ') || '',
              tags: campaignData.tagIds?.join(', ') || '',
              subject: campaignData.subject || '',
              senderName: campaignData.senderProfile?.senderName || '',
              senderEmail: campaignData.senderProfile?.senderEmail || '',
              selectedTemplateIndex: templateIndex,
              selectionType: initialSelectionType,
              selectedContacts: initialSelectedContacts,
            });
          } else if (templates.length > 0) {
            setSelectedTemplate(0);
          }
        } else if (templates.length > 0) {
          setSelectedTemplate(0);
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to load templates');
        // console.error('Error loading templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [campaignData, isEditMode]);

  // ⭐ Helper that restores everything to the original snapshot
  const resetToOriginal = () => {
    if (!originalCampaign) return;

    setCampaignTitle(originalCampaign.title);
    setReceiver(originalCampaign.receiver);
    setTags(originalCampaign.tags);
    setSubject(originalCampaign.subject);
    setSenderName(originalCampaign.senderName);
    setSenderEmail(originalCampaign.senderEmail);
    setSelectedTemplate(originalCampaign.selectedTemplateIndex);
    setSelectionType(originalCampaign.selectionType);
    setSelectedContacts(originalCampaign.selectedContacts);
    setSearchTerm('');
    setSelectedCategories(['All templates']);
  };

  // ⭐ UPDATED handleNext – reset changes the moment user clicks Next on Set Context
  const handleNext = () => {
    if (selectedTemplate !== null && templatesData[selectedTemplate]) {
      // console.log('✅ Selected Template ID:', templatesData[selectedTemplate].id);
    }

    if (step < steps.length - 1) setStep(step + 1);
  };

  // Returns to the previous step
  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // Filters templates based on selected categories and search term
  const filteredTemplates = useMemo(() => {
    return templatesData.filter((tpl) => {
      const category = getTemplateCategory(tpl);
      const matchesCategory =
        selectedCategories.includes('All templates') ||
        selectedCategories.includes(category);
      const matchesSearch = tpl.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategories, templatesData]);

  // Get the selected template ID
  const selectedTemplateId = useMemo(() => {
    if (selectedTemplate !== null && templatesData[selectedTemplate]) {
      return templatesData[selectedTemplate].id;
    }
    return undefined;
  }, [selectedTemplate, templatesData]);

  // ⭐ Get the selected template object
  const selectedTemplateObject = useMemo(() => {
    if (selectedTemplate !== null && templatesData[selectedTemplate]) {
      return templatesData[selectedTemplate];
    }
    return null;
  }, [selectedTemplate, templatesData]);

  const getSelectedTemplateImg = (): string => {
    if (selectedTemplate !== null && templatesData[selectedTemplate]) {
      return templatesData[selectedTemplate]?.thumbnailUrl || templates1;
    }
    return templates1;
  };

  // Check if template has changed - ensure it's a boolean
  const hasTemplateChanged = !!(isEditMode && originalTemplateId && selectedTemplateId !== originalTemplateId);



  return (
    <div className='p-2'>
      {/* Back button */}
      <button
        onClick={() => setShowUnsavedModal(true)}
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Campaigns
      </button>

      {/* Modal */}
      {showUnsavedModal && (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50'>
          <div className='bg-white rounded-lg shadow-lg p-6 w-[576px] relative'>
            <h2 className='text-lg font-semibold text-[#3F5265] mb-2' style={{ fontSize: '20px' }}>
              Unsaved Changes!
            </h2>
            <p className='text-[#657584] mb-6 text-sm' style={{ fontSize: '16px' }}>
              You have unsaved changes. If you leave now, your edits will be
              lost. Are you sure you want to discard them?
            </p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => navigate('/campaigns')}
                className='px-4 py-2 border border-red-400 text-red-500 rounded-lg font-medium hover:bg-red-50'
              >
                Close and Discard
              </button>
              <button
                onClick={() => setShowUnsavedModal(false)}
                className='px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600'
              >
                {isEditMode ? 'Resume editing' : 'Resume creating'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with stepper */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2
            className='text-2xl font-bold text-gray-800'
            style={{ color: '#3F5265', fontSize: '20px' }}
          >
            {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
          </h2>
          <p className='text-gray-500 mb-6' style={{ color: '#657584', fontSize: '16px' }}>
            {isEditMode ? 'Update your campaign to reach your audience' : 'Set up your campaign to reach your audience'}
          </p>
        </div>

        {/* Stepper */}
        <div className='flex items-center mb-6'>
          {steps.map((label, index) => {
            const isActive = step === index;
            const isCompleted = step > index;

            return (
              <div key={index} className='flex items-center'>
                <button
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
                    ${isActive ? 'bg-[#00899E] text-white' : ''}
                    ${isCompleted ? 'border border-[#00899E] text-[#00899E] bg-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-[#AFBBCC] text-white' : ''}
                  `}
                  style={{ fontSize: '12px' }}
                >
                  {isCompleted && (
                    <FaCheckComponent className='text-[#00899E]' size={17} />
                  )}
                  {label}
                </button>
                {index !== steps.length - 1 && (
                  <div
                    className={`h-[2px] w-12 ${step > index ? 'bg-[#00899E]' : 'bg-[#AFBBCC]'}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Section */}
      <div className='bg-white rounded-xl p-6 border border-gray-200 shadow-sm'>
        {loading ? (
          <div className='flex justify-center items-center py-12'>
            <div className='text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto'></div>
              <p className='mt-4 text-gray-600'>Loading templates...</p>
            </div>
          </div>
        ) : step === 0 ? (
          <SelectTemplate
            templates={filteredTemplates}
            categories={categories}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            selectedCategory={selectedCategories}
            setSelectedCategory={setSelectedCategories}
            handleNext={handleNext}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isEditMode={isEditMode}
          />
        ) : step === 1 ? (
          <SetContext
            campaignTitle={campaignTitle}
            setCampaignTitle={setCampaignTitle}
            receiver={receiver}
            setReceiver={setReceiver}
            tags={tags}
            setTags={setTags}
            subject={subject}
            setSubject={setSubject}
            senderName={senderName}
            setSenderName={setSenderName}
            senderEmail={senderEmail}
            setSenderEmail={setSenderEmail}
            selectedTemplateImg={getSelectedTemplateImg()}
            templateId={selectedTemplateId}
            handleBack={handleBack}
            handleNext={handleNext}
            showFullForm={isEditMode}
            campaignId={campaignId}
            isEditMode={isEditMode}
            hasTemplateChanged={hasTemplateChanged}
            showNextButton={isEditMode}
            selectionType={selectionType}
            setSelectionType={setSelectionType}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
          />
        ) : step === 2 ? (
          <ConfirmSend
            campaignTitle={campaignTitle}
            receiver={receiver}
            tags={tags}
            subject={subject}
            senderName={senderName}
            senderEmail={senderEmail}
            selectedTemplateImg={getSelectedTemplateImg()}
            handleBack={handleBack}
            campaignId={campaignId}
            template={selectedTemplateObject}
            selectionType={selectionType}
            selectedContacts={selectedContacts}
          />
        ) : null}
      </div>
    </div>
  );
};

export default CreateCampaign;